import { Fragment, useContext, useEffect, useState } from "react";
import { Formik } from "formik";
import dataConstants from "../../constants/dataConstants";
import validations from "../../services/validations";
import CustomText from "../atomic/CustomText";
import ErrorBoundary from "../global/ErrorBoundary";
import Button from "../organism/Button";
import TickIcon from "../../assets/svg/TickIcon";
import PersonalInfo from "../formsFields/PersonalInfo";
import OnlineOrderPopupValues from "../../services/models/formModels/onlineOrderPopupValues.model";
import SecurityInfoFields from "../formsFields/SecurityInfo";
import ContactInfoFields from "../formsFields/ContactInfo";
import OnlineOrderFormProps from "../../services/models/formModels/OnlineOrderForm.model";
import PatientSelectionPopup from "./patientSelectionPopup";
import { PopUpContext } from "../../services/context/popupContext";
import { useHistory, useLocation } from "react-router-dom";
import useCreatePatient from "../../services/utils/custom hooks/Patients/useCreatePatient";
import useEditPatient from "../../services/utils/custom hooks/Patients/useEditPatient";
import { useAdminContext } from "../../services/context/TourPopupContext";
import WarningBanner from "../global/WarningBanner";
import Banner from "../atomic/Banner";
import * as Yup from "yup";
import useStateAndCity from "../../services/utils/custom hooks/useStateAndCity";

const options = [
  {
    text: "Personal Info",
    stepValidation: validations.patientPersonalInfoValidation,
  },
  {
    text: "Contact Info",
    stepValidation: validations.patientContactInfoValidation,
  },
  {
    text: "Security",
    stepValidation: validations.patientSecurityInfoValidation,
  },
];

interface FormFieldsProps extends OnlineOrderFormProps {
  step: number;
}

function FormFields({
  step,
  ...props
}: Readonly<FormFieldsProps>): JSX.Element {
  if (step === 3) {
    return <SecurityInfoFields {...props} />;
  }
  if (step === 2) {
    return <ContactInfoFields {...props} />;
  }
  return <PersonalInfo {...props} />;
}

function FormFooter({
  handleNext,
  handleBack,
  step,
  disabled,
  submitting,
  onlineOrder,
  submissionText,
}: Readonly<{
  step: number;
  handleBack: () => void;
  handleNext: () => void;
  disabled: boolean;
  submitting: boolean;
  onlineOrder: boolean;
  submissionText?: string;
}>): JSX.Element {
  const { isTourActive } = useAdminContext();
  return (
    <div className="flex items-center justify-between border-t-2 h-16 z-0 sticky bottom-0 bg-white ">
      {onlineOrder && step === 1 ? null : (
        <Button
          textInformation={{
            text: "BACK",
            fontWeight: "font-bold",
            fontSize: "md:text-base text-xs",
          }}
          onClick={handleBack}
          buttonTheme="secondary"
          className="mt-4 mr-2"
          type={"button"}
          disabled={isTourActive}
        />
      )}
      <Button
        textInformation={{
          text:
            step === 3
              ? !!submissionText
                ? submissionText
                : "Proceed to order"
              : "next",
          fontWeight: "font-bold",
          fontSize: "md:text-base text-xs",
        }}
        onClick={handleNext}
        buttonTheme="primary"
        className="mt-4"
        disabled={
          // (step === 3 && disabled) ||
          disabled || submitting || isTourActive
        }
        loading={submitting}
        type={"button"}
      />
    </div>
  );
}

function StepItem({
  option,
  index,
  getFieldMeta,
  values,
  formValid,
  currentStep,
  setValidSteps,
}: Readonly<{
  option: any;
  index: number;
  getFieldMeta: any;
  values: any;
  formValid: boolean;
  currentStep: number;
  setValidSteps: (validSteps: any) => void;
}>) {
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [sectionTouched, setSectionTouched] = useState(false);
  const stepIndex = index + 1; // Step 1 = index 0, Step 2 = index 1, Step 3 = index 2

  // Determine if the step should be validated
  const shouldValidate = stepIndex <= currentStep;

  // Step is valid if:
  // - For Step 1: No errors and fields touched (required fields)
  // - For Step 2 & 3: Reached or passed the step (no required fields)
  const stepValid =
    shouldValidate && (stepIndex === 1 ? !hasErrors && sectionTouched : true);

  // another condition to make sure that every step is checked during updating the patient details
  const stepValidUpdatePatient = (!hasErrors && sectionTouched) || formValid;
  // values?.id if patient exist and we're updating that patient
  const isStepValid = values?.id ? stepValidUpdatePatient : stepValid;

  const keys =
    stepIndex === 1
      ? {
          firstName: "",
          lastName: "",
          birthDate: "",
          gender: "",
        }
      : stepIndex === 2
      ? {
          email: "",
          address: "",
          phone: "",
          city: "",
          state: "",
          zipcode: "",
        }
      : {
          "securityInfo.ssn": "",
          "securityInfo.mrn": "",
        };

  useEffect(() => {
    if (shouldValidate) {
      option.stepValidation
        .validate(values)
        .then(() => {
          setHasErrors(false);
          setValidSteps((prevState: any) => ({
            ...prevState,
            [stepIndex]: true,
          }));
        })
        .catch((errors: any) => {
          setHasErrors(true);
          setValidSteps((prevState: any) => ({
            ...prevState,
            [stepIndex]: false,
          }));
        });

      setSectionTouched(
        Object.keys(keys).some((key) => getFieldMeta(key).touched)
      );
    }
  }, [values, getFieldMeta, option.stepValidation, shouldValidate]);

  return (
    <div key={option.text} className="flex md:flex-row flex-col items-center">
      <TickIcon
        fill={isStepValid ? `#18B5B7` : "none"}
        stroke={isStepValid ? "white" : "#d0d4db"}
        className=""
      />
      <CustomText
        fontWeight="font-normal"
        text={option.text}
        className="text-xs md:text-sm ml-2"
      />
    </div>
  );
}

function Steps({
  getFieldMeta,
  values,
  formValid,
  currentStep,
  setValidSteps,
}: Readonly<{
  getFieldMeta: any;
  values: any;
  formValid: boolean;
  currentStep: number;
  setValidSteps: (validSteps: any) => void;
}>): JSX.Element {
  return (
    <div className="border-t-2 border-b-2 h-16 flex items-center justify-between md:px-32 sticky top-0 bg-white z-20">
      {options.map((option, index) => (
        <StepItem
          key={option.text}
          option={option}
          getFieldMeta={getFieldMeta}
          index={index}
          values={values}
          formValid={formValid}
          currentStep={currentStep}
          setValidSteps={setValidSteps}
        />
      ))}
    </div>
  );
}

function CreatePatientPopup({
  oktaAuth,
  patientInformation,
  setOnlineOrderValues,
  initialStep,
  values,
  createPatientFlow,
}: Readonly<{
  oktaAuth: any;
  patientInformation?: OnlineOrderPopupValues;
  setOnlineOrderValues?: (patientInformation: OnlineOrderPopupValues) => void;
  initialStep?: number;
  values?: any;
  createPatientFlow?: boolean;
}>) {
  const [step, setStep] = useState(initialStep ? initialStep : 1);
  const { data: statesData } = useStateAndCity();
  const history = useHistory();
  const [initialValues, setInitialValues] = useState<OnlineOrderPopupValues>({
    ...dataConstants.onlineOrderPopupInitialvalues,
  });
  const location = useLocation();
  const isCreatingPatient = location.pathname.includes("/access/patients");

  const { visible, setVisible, setPopUp, setPopUpTitle } =
    useContext(PopUpContext);
  const { mutate, isPending, isSuccess } = useCreatePatient(oktaAuth);
  const {
    mutate: updatePatient,
    isPending: updatingPatient,
    isSuccess: patientUpdateSuccess,
  } = useEditPatient(oktaAuth);

  const isPatientsScreen = history.location.pathname.includes("patients");
  const { isTourActive } = useAdminContext();
  const [validSteps, setValidSteps] = useState<any>({
    1: false,
    2: true,
    3: true,
  });

  useEffect(() => {
    if (patientInformation) {
      setInitialValues((prevState) => ({
        ...prevState,
        ...patientInformation,
      }));
    }
  }, []);

  useEffect(() => {
    if (isSuccess || patientUpdateSuccess) {
      setVisible(false);
    }
  }, [isSuccess, patientUpdateSuccess]);

  return (
    <div
      className={`overflow-auto xs:h-[50vh] md:h-auto max-w-[85vw] lg:w-[800px] z-0 ${
        step === 3
          ? "md:min-h-[350px]"
          : values?.testTypeId === 8 && step === 2
          ? "md:min-h-[670px]"
          : "md:min-h-[600px]"
      }`}
    >
      <ErrorBoundary>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          isInitialValid={patientInformation ? undefined : false}
          initialTouched={
            patientInformation
              ? {
                  firstName: !!patientInformation.firstName,
                  lastName: !!patientInformation.lastName,
                  address: !!patientInformation.address,
                  middleInitial: !!patientInformation.middleInitial,
                  birthDate: !!patientInformation.birthDate,
                  gender: !!patientInformation.gender,
                  securityInfo: {
                    ssn: !!patientInformation.securityInfo?.ssn,
                    mrn: !!patientInformation.securityInfo?.mrn,
                  },
                }
              : {}
          }
          validationSchema={validations.patientInformationValidation.shape({
            ...validations.patientInformationValidation.fields,
            state: Yup.string().test(
              "is-valid-state",
              "State must be selected from the list",
              (value) => {
                const state = statesData?.find(
                  (state: any) => state.state_code === value
                );

                if (!!value === false) return true;
                return !!state;
              }
            ),
            gender: Yup.string().test(
              "is-valid-gender",
              "Gender must be selected from the list",
              (value) => {
                const matchedGender = ["male", "female"].find(
                  (g) => g.toLowerCase() === value?.toLowerCase()
                );

                if (!!value === false) return true;
                return !!matchedGender;
              }
            ),
          })}
          validateOnMount={!!patientInformation}
          onSubmit={(data: OnlineOrderPopupValues) => {
            if (isPatientsScreen) {
              if (data.id) {
                updatePatient(data);
              } else mutate(data);
            } else if (setOnlineOrderValues) {
              // Editing patient from within online order form
              setVisible(false);
              setOnlineOrderValues(data);
            } else {
              // Creating new patient from global search or other places
              // Navigate to online-order screen with patient information
              // Remove id if it exists (new patient shouldn't have id)
              const patientData = { ...data };
              if (!patientData.id) {
                delete patientData.id;
              }
              setVisible(false);
              history.push(`/access/online-order`, {
                patientInformation: JSON.stringify(patientData),
              });
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            submitForm,
            setFieldValue,
            getFieldMeta,
            isValid,
          }) => {
            function handleNext() {
              if (step === 3) {
                // Use submitForm to programmatically submit the form
                submitForm();
              } else setStep((prevState) => prevState + 1);
            }
            return (
              <Fragment>
                <Steps
                  getFieldMeta={getFieldMeta}
                  values={values}
                  formValid={isValid}
                  currentStep={step}
                  setValidSteps={setValidSteps}
                />
                {isCreatingPatient && (
                  <Banner
                    text={
                      "Please review the patient's data carefully before submitting. If any of their future orders are submitted to Castle, their information cannot be updated via the portal."
                    }
                    type="cancel"
                    // onClose={hideBanner}
                    // onClick={handleReOrder}
                    hideButton={false}
                  />
                )}
                <CustomText
                  fontWeight="font-semibold"
                  text={
                    step === 1
                      ? "Patient Personal Info"
                      : step === 2
                      ? "Patient Contact Info"
                      : "Security Info"
                  }
                  className="my-4 mt-6"
                />
                {step === 3 && (
                  <CustomText
                    fontWeight="font-normal"
                    className="text-sm md:text-md"
                    text="For test submission, either the SSN or MRN is highly recommended. You may still provide both of the information if you wish."
                  />
                )}
                <form
                  onSubmit={handleSubmit}
                  className={`flex flex-col justify-center ${
                    step === 1 && " overflow-auto md:min-h-[500px]"
                  }`} // Ensure the form takes full height
                  // style={{ minHeight: {`${step === 1 && '500px'}`} }} // Set a minimum height for the form
                >
                  <div
                    className={`overflow-visible flex flex-wrap  ${
                      // Allow content to scroll if it overflows
                      step === 3 ? "md:min-h-[120px]" : "md:min-h-[425px]"
                    }`}
                  >
                    {values?.testTypeId === 8 && step === 2 && (
                      <div className="text-royalBlue">
                        <CustomText
                          text={"Note: "}
                          fontWeight="font-semibold"
                          className="text-left"
                        />
                        <CustomText
                          text={`If this is a telehealth order and you'd like your
                          patient to receive alerts about their kit, please
                          enter patient email.`}
                          fontWeight="font-normal"
                          className="text-left mt-1"
                        />
                      </div>
                    )}
                    <FormFields
                      step={step}
                      touched={touched}
                      errors={errors}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                      values={values}
                      setFieldValue={setFieldValue}
                      getFieldMeta={getFieldMeta}
                      disabled={isTourActive}
                    />
                  </div>
                  <FormFooter
                    step={step}
                    handleNext={handleNext}
                    disabled={!validSteps[step] || isTourActive}
                    submitting={isPending || updatingPatient}
                    onlineOrder={!!patientInformation}
                    submissionText={
                      isPatientsScreen
                        ? values?.id
                          ? "Update"
                          : "Create"
                        : undefined
                    }
                    handleBack={() => {
                      if (step === 1) {
                        setVisible(!visible);
                        setPopUpTitle("Choose or Create a Patient");
                        setPopUp(<PatientSelectionPopup oktaAuth={oktaAuth} />);
                      } else {
                        setStep((prevState) => prevState - 1);
                      }
                    }}
                  />
                </form>
              </Fragment>
            );
          }}
        </Formik>
      </ErrorBoundary>
    </div>
  );
}

export default CreatePatientPopup;
